import React from 'react';
import Icon from '../../../components/AppIcon';

const ExplorationFieldCard = ({ field, userPoints, onClick, onUnlock }) => {
  const canUnlock = userPoints >= field?.requiredPoints;
  const progressPercentage = field?.explorationProgress || 0;

  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    amber: 'from-amber-500 to-amber-600',
    purple: 'from-purple-500 to-purple-600'
  };

  const demandColors = {
    high: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    low: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
  };

  const demandLabels = {
    high: 'Forte Demande',
    medium: 'Demande Moyenne',
    low: 'Demande Faible'
  };

  return (
    <div
      onClick={onClick}
      className={`relative bg-card rounded-xl border-2 transition-all duration-300 overflow-hidden ${
        field?.isUnlocked
          ? 'border-border hover:border-primary hover:shadow-lg cursor-pointer'
          : 'border-dashed border-muted-foreground/30 opacity-75'
      }`}
    >
      {/* Lock Overlay */}
      {!field?.isUnlocked && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/80 to-gray-900/60 backdrop-blur-sm z-10 flex flex-col items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center mb-3 border-2 border-white/20">
            <Icon name="Lock" size={32} className="text-white" />
          </div>
          <p className="text-white font-semibold text-lg mb-1">Verrouillé</p>
          <p className="text-white/80 text-sm mb-4">{field?.requiredPoints} points requis</p>
          {canUnlock && (
            <button
              onClick={(e) => {
                e?.stopPropagation();
                onUnlock();
              }}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-lg font-medium hover:shadow-lg transition-all"
            >
              Débloquer Maintenant
            </button>
          )}
          {!canUnlock && (
            <p className="text-white/60 text-xs">
              Encore {field?.requiredPoints - userPoints} points nécessaires
            </p>
          )}
        </div>
      )}
      {/* Card Content */}
      <div className="p-5">
        {/* Header with Icon */}
        <div className="flex items-start justify-between mb-4">
          <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${colorClasses?.[field?.color]} flex items-center justify-center shadow-md ${
            field?.isUnlocked ? 'group-hover:scale-110 transition-transform' : ''
          }`}>
            <Icon name={field?.icon} size={28} className="text-white" />
          </div>
          {field?.isUnlocked && (
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${demandColors?.[field?.demandLevel]}`}>
              {demandLabels?.[field?.demandLevel]}
            </div>
          )}
        </div>

        {/* Field Name */}
        <h3 className="text-lg md:text-xl font-bold text-foreground mb-2 line-clamp-2">
          {field?.name}
        </h3>

        {/* Description */}
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
          {field?.description}
        </p>

        {/* Career Highlights */}
        {field?.isUnlocked && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Briefcase" size={14} className="text-primary" />
              <span className="text-xs font-semibold text-foreground">Carrières Principales</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {field?.careerHighlights?.slice(0, 2)?.map((career, index) => (
                <span key={index} className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded">
                  {career}
                </span>
              ))}
              {field?.careerHighlights?.length > 2 && (
                <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded">
                  +{field?.careerHighlights?.length - 2}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Salary Range */}
        {field?.isUnlocked && (
          <div className="flex items-center gap-2 mb-4 text-sm">
            <Icon name="Coins" size={16} className="text-amber-600" />
            <span className="text-foreground font-medium">{field?.averageSalary}</span>
          </div>
        )}

        {/* Progress Bar */}
        {field?.isUnlocked && (
          <div className="mb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground">Progression d'Exploration</span>
              <span className="text-xs font-bold text-primary">{progressPercentage}%</span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full bg-gradient-to-r ${colorClasses?.[field?.color]} transition-all duration-500`}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        )}

        {/* Reward Points */}
        {field?.isUnlocked && (
          <div className="flex items-center justify-between pt-3 border-t border-border">
            <div className="flex items-center gap-2">
              <Icon name="Star" size={16} className="text-amber-500" />
              <span className="text-xs text-muted-foreground">Récompense</span>
            </div>
            <span className="text-sm font-bold text-amber-600">+{field?.rewardPoints} pts</span>
          </div>
        )}
      </div>
      {/* Unlock Animation Border */}
      {field?.isUnlocked && progressPercentage < 100 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 opacity-50" />
      )}
    </div>
  );
};

export default ExplorationFieldCard;